import api from '../../config/axios';
import { setFinanceContext } from '../../services/financeScope';

const scopes = ['self', 'partner', 'couple'];
let latestFetch = 0;
let preferenceQueue = Promise.resolve();

const state = { enabled: false, couple: null, partner: null, invitations: [], notices: [], price: null,
    billingName: '', scope: 'self', goalsScope: 'self', revision: 0, accountId: null, userId: null, error: '',
    savedScope: 'self', preferenceSaving: false, preferenceError: '', preferenceVersion: 0, preferenceBlocked: false };
const getters = {
    getCouple: s => s.couple, getCouplePartner: s => s.partner, getCoupleEnabled: s => s.enabled,
    getCoupleInvitations: s => s.invitations, getCoupleNotices: s => s.notices,
    getCouplePrice: s => s.price, getCoupleBillingName: s => s.billingName,
    getFinanceScope: s => s.scope, getGoalsScope: s => s.goalsScope, getAccountViewRevision: s => s.revision,
    getFinancePreferenceSaving: s => s.preferenceSaving, getFinancePreferenceError: s => s.preferenceError,
};
const actions = {
    async fetchCouple({ state, commit, dispatch, rootGetters }) {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        const requestId = ++latestFetch;
        const userId = String(rootGetters.getId || '');
        const accountId = String(rootGetters.getCompany_id || '');
        const version = state.preferenceVersion;
        const wasSaving = state.preferenceSaving;
        const response = await api.axios.get('/api/couple');
        if (requestId !== latestFetch || token !== localStorage.getItem('auth_token')
            || userId !== String(rootGetters.getId || '')) return;
        const data = response.data || {};
        const changed = state.userId !== userId || state.accountId !== accountId
            || (state.couple?.id || null) !== (data.couple?.id || null);
        const scope = data.enabled && data.couple
            ? (scopes.includes(data.preferred_scope) ? data.preferred_scope : 'couple') : 'self';
        commit('setCouple', { data, accountId, userId });
        if (changed) {
            commit('resetFinancePreference', scope);
            await dispatch('resetCoupleViews', { scope, restorePreference: true });
        } else if (!wasSaving && !state.preferenceSaving && !state.preferenceError && !state.preferenceBlocked
            && version === state.preferenceVersion) {
            commit('setSavedFinanceScope', scope);
            if (state.scope !== scope) await dispatch('applyFinanceScope', scope);
        }
        return data;
    },
    invalidateCoupleContent({ state, commit }) {
        commit('changeCoupleView', { scope: state.scope, goals: state.goalsScope });
    },
    resetCoupleViews({ commit }, { scope = 'self', restorePreference = false } = {}) {
        commit('setFinancePreferenceBlocked', !restorePreference);
        commit('changeCoupleView', { scope, goals: 'self' });
        commit('clearSharedFinance');
        commit('setFinancialGoals', []);
        commit('setFinancialAdvisor', null);
        commit('setSummary', null);
        commit('setInvoiceHistory', []);
    },
    applyFinanceScope({ state, commit }, scope) {
        commit('clearSharedFinance');
        commit('setFinancialAdvisor', null);
        commit('changeCoupleView', { scope, goals: state.goalsScope });
    },
    selectFinanceScope({ state, commit, dispatch, rootGetters }, scope) {
        if (!scopes.includes(scope) || (scope !== 'self' && !state.couple)) return;
        if (scope === state.scope && !state.preferenceError && (state.preferenceSaving
            || (scope === state.savedScope && !state.preferenceBlocked))) return;
        dispatch('applyFinanceScope', scope);
        if (!state.couple) return;

        commit('beginFinancePreferenceSave');
        const version = state.preferenceVersion;
        const userId = state.userId;
        const coupleId = state.couple.id;
        const token = localStorage.getItem('auth_token');
        const current = () => version === state.preferenceVersion && userId === state.userId
            && userId === String(rootGetters.getId || '') && coupleId === state.couple?.id
            && token === localStorage.getItem('auth_token');

        // Serialize saves so the final selection wins even when responses arrive slowly.
        const save = preferenceQueue.catch(() => undefined).then(async () => {
            if (!current()) return;
            try {
                await api.axios.patch('/api/couple/preference', { scope, couple_id: coupleId },
                    { timeout: 10000, authTokenSnapshot: token });
                if (current()) commit('setSavedFinanceScope', scope);
            } catch (error) {
                if (current()) commit('setFinancePreferenceError',
                    'Sua visão foi alterada, mas a preferência não foi salva. Tente novamente.');
            } finally {
                if (current()) commit('setFinancePreferenceSaving', false);
            }
        });
        preferenceQueue = save;
        return save;
    },
    selectGoalsScope({ state, commit }, goals) {
        if (!['self', 'couple'].includes(goals) || (goals !== 'self' && !state.couple)) return;
        commit('setFinancialGoals', []);
        commit('changeCoupleView', { scope: state.scope, goals });
    },
    async sendCoupleInvitation({ dispatch }, data) {
        await api.axios.post('/api/couple/invitations', data); await dispatch('fetchCouple');
    },
    async respondCoupleInvitation({ dispatch }, { id, action }) {
        await api.axios.post(`/api/couple/invitations/${id}/respond`, { action, ...(action === 'accept' ? { consent: true } : {}) });
        await dispatch('fetchCouple');
    },
    async unlinkCouple({ dispatch }, password) {
        await api.axios.post('/api/couple/unlink', { password });
        await dispatch('resetCoupleViews'); await dispatch('fetchCouple');
    },
};
const mutations = {
    setCouple(s, { data, accountId, userId }) {
        s.enabled = !!data.enabled; s.couple = data.couple || null; s.partner = data.partner || null;
        s.invitations = data.invitations || []; s.notices = data.notices || []; s.price = data.price;
        s.billingName = data.billing_name || ''; s.accountId = accountId; s.userId = userId;
    },
    resetFinancePreference(s, scope) {
        s.savedScope = scope; s.preferenceSaving = false; s.preferenceError = '';
        s.preferenceVersion = (Number(s.preferenceVersion) || 0) + 1; s.preferenceBlocked = false;
    },
    beginFinancePreferenceSave(s) {
        s.preferenceVersion = (Number(s.preferenceVersion) || 0) + 1;
        s.preferenceSaving = true; s.preferenceError = ''; s.preferenceBlocked = false;
    },
    setSavedFinanceScope(s, scope) { s.savedScope = scope; },
    setFinancePreferenceError(s, message) { s.preferenceError = message; },
    setFinancePreferenceSaving(s, saving) { s.preferenceSaving = saving; },
    setFinancePreferenceBlocked(s, blocked) { s.preferenceBlocked = blocked; },
    changeCoupleView(s, { scope, goals }) {
        s.scope = scope; s.goalsScope = goals; s.revision += 1; setFinanceContext(scope, goals);
    },
};
export default { state, getters, actions, mutations };
